import { createSelector } from '@reduxjs/toolkit'
import { RootState } from 'shared/config/store'

export const selectAiAssistantState = (state: RootState) => state.aiAssistant

export const selectChats = createSelector(
  selectAiAssistantState,
  (state) => state.chats
)

export const selectChatMessages = createSelector(
  selectAiAssistantState,
  (state) => state.chatMessages
)

export const selectCurrentChatMessages = createSelector(
  [selectChatMessages, (_: any, chatId: string) => chatId],
  (chatMessages, chatId) => {
    const messages = chatMessages[chatId] || []
    return [...messages].sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
  }
)

export const selectMessagesLoading = createSelector(
  selectAiAssistantState,
  (state) => state.messagesLoading
)

export const selectChatMessagesLoading = createSelector(
  [selectMessagesLoading, (_: any, chatId: string) => chatId],
  (messagesLoading, chatId) => messagesLoading[chatId] || false
)

export const selectShouldShowLoadingMessage = createSelector(
  [selectCurrentChatMessages, (_: any, chatId: string) => chatId],
  (messages) => {
    if (messages.length === 0) return false

    const lastMessage = messages[messages.length - 1]

    return lastMessage.author_id !== -1
  }
)



export const selectAiAssistantStatus = createSelector(
  selectAiAssistantState,
  (state) => state.status
)

export const selectAiAssistantError = createSelector(
  selectAiAssistantState,
  (state) => state.error
)

export const selectSortedChats = createSelector(
  selectChats,
  (chats) => [...chats].sort((a, b) =>
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  )
)

export const selectAttachmentsModal = createSelector(
  selectAiAssistantState,
  (state) => state.attachmentsModal
)

export const selectAttachmentsModalOpen = createSelector(
  selectAttachmentsModal,
  (modal) => modal.isOpen
)

export const selectAttachmentsModalSection = createSelector(
  selectAttachmentsModal,
  (modal) => modal.selectedSection
)

export const selectSelectedAttachments = createSelector(
  selectAttachmentsModal,
  (modal) => modal.selectedAttachments
)

export const selectAttachmentsLoading = createSelector(
  selectAttachmentsModal,
  (modal) => modal.loadingAssets
)

export const selectAttachmentsSearchValue = createSelector(
  selectAttachmentsModal,
  (modal) => modal.searchValue
)

export const selectAttachmentsSearchResults = createSelector(
  selectAttachmentsModal,
  (modal) => modal.searchResults
)

export const selectAttachmentsSearching = createSelector(
  selectAttachmentsModal,
  (modal) => modal.isSearching
)

export const selectDraftMessage = createSelector(
  selectAiAssistantState,
  (state) => (chatId: string | null) => {
    if (!chatId) return ''
    return state.draftMessages[chatId] || ''
  }
)

export const selectDraftMessages = createSelector(
  selectAiAssistantState,
  (state) => state.draftMessages
) 

export const selectChatsIdsToShowAnimatedMessage = createSelector(
  selectAiAssistantState,
  (state) => state.chatsIdsToShowAnimatedMessage
)

export const selectAttachmentsAssets = createSelector(
  selectAttachmentsModal,
  (modal) => modal.assets
)

export const selectAttachmentsAreAllAssetsFetched = createSelector(
  selectAttachmentsModal,
  (modal) => modal.areAllAssetsFetched
)

export const selectAttachments = createSelector(
  selectAiAssistantState,
  (state) => state.attachments
)

export const selectSystemHints = createSelector(
  selectAiAssistantState,
  (state) => state.system_hints
)

export const selectItemsMarkedAsUsefullForAiAssistant = createSelector(
  selectAiAssistantState,
  (state) => state.itemsMarkedAsUsefullForAiAssistant
)